// Need to install our custom lodash methods before anything else. - ETC
import 'intersection-observer';

import { createApp } from 'vue';
import { useStorage } from '@vueuse/core';
import merge from 'lodash.merge';
import './scss/legacy-bootstrap.scss';
import '@acv/shared-vuejs/dist/shared-vuejs.css';
import './assets/css/tailwind.css';
import VueScrollto from 'vue-scrollto';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import { TailwindHelper } from '@acv/shared-vuejs';
import { directive as onClickaway } from '@/directives/clickaway';
import tailwindConfig from '../tailwind.config'; // import this project tailwind config

import App from './App.vue';
import store from './store';
import router from './router';
import { ACV_STORAGE_KEY } from './store/plugins/acvStorage';
import { restoreUserStorage } from './store/plugins/userStorage';

// Prevents circular dependency issues from bundling
import { setStore, setRouter } from './appContext';

setStore(store);
setRouter(router);

// Read persisted state once here, not from a Vuex plugin that reads on every mutation
const hydrateStoreFromStorage = async () => {
  let acvStorageState = {};
  try {
    acvStorageState = JSON.parse(useStorage(ACV_STORAGE_KEY, '{}').value);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[main] Failed to parse acv-storage, using default state', err);
  }

  delete acvStorageState.auctionAccess?.auctionAccessId;

  // Ensure any existing flags property is copied to fuseboxFlags so existing code still works
  if (acvStorageState.featureFlags?.flags) {
    acvStorageState.featureFlags.fuseboxFlags = { ...acvStorageState.featureFlags.flags };
  }

  const userStorageState = await restoreUserStorage(store.state.authorization);

  store.replaceState(merge(store.state, acvStorageState, userStorageState));
};

const app = createApp(App);

app.config.warnHandler = (msg, vm, trace) => {
  // eslint-disable-next-line no-console
  console.error(`[Vue warn]: ${msg}${trace}`);
};

app.use(router);
app.use(store);
app.use(VueScrollto);
app.use(TailwindHelper, {
  tailwindConfig,
});
app.use(VueReCaptcha, {
  siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  loaderOptions: {
    // We are required to show messaging as part of flows that use ReCAPTCHA
    // when hiding the reCAPTCHA badge. See:
    // https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
    autoHideBadge: true,
  },
});
app.directive('onClickaway', onClickaway);

// Sync initial route state to Vuex
store.dispatch('updateRoute', { to: router.currentRoute.value, from: {} });

// setup store with persisted state before app mount
await hydrateStoreFromStorage();
app.mount('#app');
